import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { supabase } from '../utils/supabase';

interface CustomLessonRequestModalProps {
  level: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function CustomLessonRequestModal({ level, onClose, onSuccess }: CustomLessonRequestModalProps) {
  const [dutchText, setDutchText] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!dutchText.trim()) {
      setError('Please enter some Dutch text');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('You must be logged in to submit a request');
        return;
      }

      const { error: insertError } = await supabase
        .from('custom_lesson_requests')
        .insert({
          user_id: user.id,
          level,
          dutch_text: dutchText.trim(),
          notes: notes.trim() || null,
          status: 'pending'
        });

      if (insertError) throw insertError;

      onSuccess();
    } catch (err) {
      console.error('Error submitting custom lesson request:', err);
      setError('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Request Custom Lesson</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <p className="text-gray-700 mb-4">
              Have a Dutch text you need to study? Submit it here and we'll create a custom lesson for you!
              This is a great way to learn Dutch while studying material you already need to read.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Level: <span className="font-semibold">{level}</span>
            </p>
          </div>

          <div>
            <label htmlFor="dutchText" className="block text-sm font-medium text-gray-700 mb-2">
              Dutch Text *
            </label>
            <textarea
              id="dutchText"
              value={dutchText}
              onChange={(e) => setDutchText(e.target.value)}
              placeholder="Paste your Dutch text here..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={10}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {dutchText.length} characters
            </p>
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes (Optional)
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any specific requests or context about this text..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={3}
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5" />
                  Submit Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
